import {syncSavedDrafts} from "../shared/offline/syncSavedDrafts";
import type { SessionResponse } from "@novel/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useMemo,useEffect,useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ApiError, getSession, logout as logoutRequest } from "../shared/api/client";

import {verifiedSession,cachedSession,forgetSession,sessionEpoch,isExplicitlySignedOut} from "../shared/offline/cache";
import {SessionContext,type SessionState} from "./sessionContext";

/**
 * 会话状态只有一个来源：服务端 /api/auth/session。
 * 离线时仅使用 IndexedDB 中最近验证且未过期的账号身份读取本机文件；不把它当作服务端授权。
 *
 * 未登录（401）在这里被归一成 null，而不是错误状态：
 * 它是正常状态，登录页不应该因此显示报错。
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [offline,setOffline]=useState(false);
  const [isSigningOut,setSigningOut]=useState(false);

  const query = useQuery<SessionResponse | null>({
    queryKey: ["session"],
    networkMode:"always",
    queryFn: async ({signal}) => {
      const epoch=sessionEpoch();
      if(isExplicitlySignedOut())return null;
      try {
        const value=await getSession(signal);
        if(signal.aborted||!await verifiedSession(value,epoch))return null;
        setOffline(false);return value;
      } catch (error) {
        if(signal.aborted||epoch!==sessionEpoch())return null;
        if (error instanceof ApiError && error.status === 401){await forgetSession();setOffline(false);return null;}
        if(!(error instanceof ApiError)||[502,503,504].includes(error.status)){const value=await cachedSession(epoch);if(value){setOffline(true);return value;}}
        throw error;
      }
    },
    retry: false,
    refetchInterval: offline ? 15_000 : false,
    staleTime: 30_000
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["session"] });
  }, [queryClient]);

  useEffect(()=>{
    const refreshOnline=()=>{void queryClient.invalidateQueries({queryKey:["session"]});};
    const changed=(event:StorageEvent)=>{
      if(!["novel.sessionVersion","novel.explicitLogout"].includes(event.key??""))return;
      void queryClient.cancelQueries().then(()=>{
        queryClient.setQueryData(["session"],null);
        queryClient.removeQueries({predicate:query=>query.queryKey[0]!=="session"});
        refreshOnline();
      });
    };
    window.addEventListener("online",refreshOnline);window.addEventListener("focus",refreshOnline);window.addEventListener("storage",changed);
    return()=>{window.removeEventListener("online",refreshOnline);window.removeEventListener("focus",refreshOnline);window.removeEventListener("storage",changed);};
  },[queryClient]);
  useEffect(()=>{
    if(!query.data?.account||offline)return;const id=query.data.account.id;let running=false;
    const sync=()=>{if(running)return;running=true;void syncSavedDrafts(id).then(result=>{if(result.synchronized){void queryClient.invalidateQueries({queryKey:["works"]});}}).catch(()=>undefined).finally(()=>{running=false;});};
    sync();const timer=setInterval(sync,15000);return()=>clearInterval(timer);
  },[query.data?.account.id,offline,queryClient]);
  const signOut = useCallback(async () => {
    setSigningOut(true);
    const forgotten=forgetSession(true);
    // Dispatch with the old session before the login form becomes available.
    const serverLogout=logoutRequest().catch(error=>{console.warn("服务端登出失败，已清除本地会话",error);});
    await queryClient.cancelQueries();
    setOffline(false);
    queryClient.setQueryData<SessionResponse | null>(["session"], null);
    queryClient.removeQueries({predicate: candidate=>candidate.queryKey[0]!=="session"});
    navigate("/login", { replace: true });
    await forgotten;
    await serverLogout;setSigningOut(false);

    // 必须显式把会话置为未登录。
    // 不能只调用 queryClient.clear()：活跃查询不会因此重新取数，
    // 组件也不会收到通知，界面会一直停在"已登录"（已实测复现）。
  }, [navigate, queryClient]);

  const value = useMemo<SessionState>(
    () => ({
      offline,isSigningOut,account: query.data?.account ?? undefined,
      isLoading: query.isPending,
      isAuthenticated: query.data != null,
      refresh,
      signOut
    }),
    [query.data, query.isPending, refresh, signOut,offline,isSigningOut]
  );

  return <SessionContext.Provider value={value}>{offline&&<p role="status" className="novel-offline-notice">离线模式：已下载的作品可继续编辑，本机修改将在联网并验证账号后同步。</p>}{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession 必须在 SessionProvider 内使用");
  return context;
}
